import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const navigationCode = `// Precise Navigation: The History API
// Adding a new entry to the browser stack
history.pushState(
    { view: 'product_detail', id: 402 }, // State object
    '', // Title (historical relic, mostly ignored)
    '/products/filefusion-pro' // The URL to display
);

// Replacing the current entry (useful for UI filters/toggles)
history.replaceState(
    { ...history.state, filter: 'active' },
    '',
    '?status=active'
);`;

const popstateCode = `// The Event Loop: Handling Back/Forward Actions
window.addEventListener('popstate', (event) => {
    // This fires when the user clicks 'Back' or 'Forward'
    const state = event.state;

    if (state?.view === 'product_detail') {
        renderProduct(state.id); // Reconstruct UI from the history state
    } else {
        renderDashboard();
    }
});`;

const routerCode = `// Architectural Logic: A Minimalist SPA Router
const handleRouting = (path) => {
    // 1. Update the URL without a page reload
    history.pushState({ path }, '', path);

    // 2. Trigger the UI engine to swap components
    const event = new CustomEvent('route-change', { detail: { path } });
    window.dispatchEvent(event);
};

// Intercepting global link clicks
document.addEventListener('click', (e) => {
    if (e.target.matches('a[data-link]')) {
        e.preventDefault();
        handleRouting(e.target.getAttribute('href'));
    }
});`;

const JSHistoryAPI = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Navigation & Routing</div>
                <h1>The History API: Engineering SPA Transitions</h1>
                <p className="hero-lead">
                    Master the mechanics of Single Page Application (SPA) routing. Learn to manipulate
                    the browser's session history, manage complex navigation states, and build
                    seamless, reload-free user experiences.
                </p>
            </div>

            <div className="content-section">
                <h2>The Protocol of Navigation: pushState and replaceState</h2>
                <p>
                    In a traditional web model, changing the URL requires a full request-response cycle from
                    the server. The **History API** breaks this dependency by allowing you to manually
                    push new entries onto the browser's stack. This enables you to change the URL string
                    locally, while your JavaScript engine handles the UI transformation.
                </p>
                <CodeBlock code={navigationCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>State Serialization: Beyond the URL</h2>
                <p>
                    One of the most powerful features of `pushState` is the **State Object**. This is a
                    serialized bundle of data (up to 640KB) that is permanently associated with that
                    specific history entry.
                </p>
                <ProTip><p>
                        Don't rely on the browser to remember where the user was. Store `scrollX` and
                        `scrollY` inside your state object during a `pushState` call. When the user
                        returns via the back button, you can use this data to precisely restore
                        their exact pixel position.
                    </p></ProTip>
            </div>

            <div className="content-section">
                <h2>The Synchronization Loop: popstate</h2>
                <p>
                    The `popstate` event is the gatekeeper. It fires whenever the active history entry
                    changes due to user action (like clicking the browser's Back or Forward buttons).
                    As an engineer, your job is to listen for this event and **re-render** the
                    appropriate UI component based on the data found in `event.state`.
                </p>
                <CodeBlock code={popstateCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Router Architecture: Intercepting the Click</h2>
                <p>
                    To build a truly production-grade SPA, you must intercept the default behavior of
                    anchor tags (`&lt;a&gt;`). Instead of allowing the browser to fetch a new page, you
                    prevent the default action and route the navigation through your own History
                    management logic.
                </p>
                <CodeBlock code={routerCode} language="javascript" />
            </div>

            <KeyFacts><ul>
                    <li>**Persistence:** Always provide a fallback for direct URL entry (Server-Side Routing).</li>
                    <li>**Integrity:** Store UI-specific metadata (IDs, filters) in the history state object.</li>
                    <li>**UX:** Use `replaceState` for minor UI toggles to avoid "polluting" the back button.</li>
                    <li>**Synchronization:** Ensure your UI state perfectly matches the URL at all times.</li>
                    <li>**Accessibility:** Remember to manage focus whenever a route change occurs.</li>
                    <li>**Scale:** Use the `&lt;base&gt;` tag or absolute paths to prevent nested routing path errors.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSHistoryAPI;


